import axios, { AxiosRequestConfig } from "axios";
import { persistor, store } from "../store";
import {
  logout,
  setAuth,
  setMaintenance,
} from "../store/reducers/user.reducer";
import EquimetreRequestConfig from "./equimetreRequestConfig";
import { User } from "./user";

var retry = false;

const MAINTENANCE_POLL_MS = 30_000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type MaintenanceRequestConfig = EquimetreRequestConfig & {
  _maintenance?: boolean;
};

const isMaintenanceError = (error: any) =>
  error?.response?.status === 503 &&
  error?.response?.data?.maintenance === true;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URI, // Replace with your API base URL
  headers: {
    // Accept: 'application/json'
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Modify the request config here (e.g., add headers, authentication tokens)
    const accessToken = localStorage.getItem("token");
    // ** If token is present add it to request's Authorization Header
    if (accessToken) {
      if (config.headers)
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Modify the response data here (e.g., parse, transform)
    const config = response.config as MaintenanceRequestConfig;

    // A replayed request succeeded: server is back
    if (config._maintenance) {
      store.dispatch(setMaintenance(false));
    }

    if (
      config.useHydraArrayTransform &&
      response.data &&
      response.data["hydra:member"]
    ) {
      // Transform the data to extract `hydra:member` if `useHydraTransform` is true
      response.data = response.data["hydra:member"];
    }

    return response;
  },
  async (error) => {
    // Handle response errors here

    const config = error?.config as MaintenanceRequestConfig | undefined;

    // Maintenance: wait 30s, then replay the same request until the server is back
    if (config && isMaintenanceError(error)) {
      store.dispatch(setMaintenance(true));
      config._maintenance = true;

      await sleep(MAINTENANCE_POLL_MS);

      // Request was cancelled while waiting (e.g. component unmounted)
      if (config.signal?.aborted) {
        store.dispatch(setMaintenance(false));
        return Promise.reject(error);
      }

      // Goes through this interceptor again, so it keeps looping while the 503 persists
      return axiosInstance.request(config);
    }

    // A replay failed with a different error: server is up, leave maintenance mode
    if (config?._maintenance) {
      store.dispatch(setMaintenance(false));
    }

    if (error?.response?.status === 401 && !retry) {
      retry = true; // Mark the request as retried to avoid infinite loops.

      try {
        let refresh_token = localStorage.getItem("refresh_token");

        if (!refresh_token) throw Error("No refresh token found");

        let response = await User.refreshUserToken(refresh_token);

        let accessToken = response.data.token;

        localStorage.setItem("token", response.data.token);
        store.dispatch(setAuth(true));

        axiosInstance.defaults.headers.common["Authorization"] =
          `Bearer ${accessToken}`;
      } catch (error) {
        store.dispatch(logout());
        window.localStorage.clear();
        persistor.purge();
      } finally {
        retry = false;
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
